import React from "react";
import { Col, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";

import { HookFormInputGroup, HookSelectFormGroup } from "components";
import { DebounceTimeout } from "globalConstants";
import { useBackendPlatform } from "hooks";
import { NailsoftMerchantTypes, TerminalProductNameTypes } from "types/enums";
import { trimMidSentenceNormalizer, trimNormalizer } from "validators";
import {
	maxLength20,
	maxLength50,
	normalizeAll,
	onlyAlphanumericAndExtraCharacters,
	required,
} from "validators/validation";

import PosSubscription from "./posSubscriptionSection";
import Tokenization from "./tokenization";
import Transarmor from "./transarmor";

import styles from "../terminalSolution.module.scss";

type PointOfSaleProps = {
	sectionName?: string;
	isLoading?: boolean;
	backEndPlatformType: string;
	isApplication?: boolean;
	defaultValues?: any;
	showMarketingServices?: boolean;
};

const PointOfSale = ({
	sectionName = "pointOfSale",
	isLoading = false,
	backEndPlatformType,
	isApplication,
	defaultValues,
	showMarketingServices = false,
}: PointOfSaleProps) => {
	const {
		register,
		watch,
		getValues,
		formState: { errors },
	} = useFormContext();

	const productType = watch(
		`${sectionName}.product`,
		getValues(`${sectionName}.product`),
	);
	const {
		isBackEndPlatformOmaha,
		isBackEndPlatformNorth,
		isBackEndPlatformTsys,
	} = useBackendPlatform(backEndPlatformType);

	const isOtherProduct =
		productType?.name === TerminalProductNameTypes.Other.name;

	const isNailSoftProduct =
		productType?.name === TerminalProductNameTypes.NailSoft.name;
	const isCalendariseProduct =
		productType?.name === TerminalProductNameTypes.Calendarise.name;

	const products = TerminalProductNameTypes.getOptions();

	const merchantTypes = NailsoftMerchantTypes.getOptions();

	return (
		<div className={styles.section} data-autotest="pointOfSaleSection">
			<Row>
				<Col md={5}>
					<HookSelectFormGroup
						label="Product"
						options={products}
						getOptionLabel={option => option.displayName}
						getOptionValue={option => option.name}
						placeholder="Please Select"
						pleaseSelectText="Please Select"
						name={`${sectionName}.product`}
						isSearchable={false}
						cacheOptions
						defaultOptions
						debounceTimeout={DebounceTimeout}
						blurLoading={isLoading}
						showRequiredMark
						rules={{
							validate: {
								required,
							},
						}}
					/>
				</Col>
				<Col md={{ span: 5, offset: 2 }}>
					{isBackEndPlatformOmaha || isBackEndPlatformNorth ? (
						<HookFormInputGroup
							name={`${sectionName}.authorizationVersion`}
							register={register}
							label="Authorization Version"
							colInput={6}
							colLabel={6}
							blurLoading={isLoading}
							autoComplete="off"
							normalizeOnBlur={trimNormalizer}
							normalize={normalizeAll([
								onlyAlphanumericAndExtraCharacters,
								trimMidSentenceNormalizer,
							])}
							validator={{ maxLength20 }}
							errors={errors}
						/>
					) : null}
				</Col>
				{isOtherProduct && (
					<Col md={5}>
						<HookFormInputGroup
							name={`${sectionName}.nameIfOther`}
							register={register}
							label="Name"
							colInput={6}
							colLabel={6}
							blurLoading={isLoading}
							autoComplete="off"
							normalizeOnBlur={trimNormalizer}
							normalize={normalizeAll([
								onlyAlphanumericAndExtraCharacters,
								trimMidSentenceNormalizer,
							])}
							validator={{ maxLength50, required }}
							showRequiredMark
							errors={errors}
						/>
					</Col>
				)}
			</Row>

			{(isNailSoftProduct || isCalendariseProduct) && (
				<>
					{isApplication && (
						<Row>
							<Col md={5}>
								<HookSelectFormGroup
									label="Merchant Type"
									options={merchantTypes}
									getOptionLabel={option =>
										option.displayName
									}
									getOptionValue={option => option.name}
									placeholder="Please Select"
									pleaseSelectText="Please Select"
									name={`${sectionName}.merchantType`}
									isSearchable={false}
									cacheOptions
									defaultOptions
									debounceTimeout={DebounceTimeout}
									blurLoading={isLoading}
								/>
							</Col>
						</Row>
					)}
					<p className={styles.title}>Subscription Options</p>
					<PosSubscription
						sectionName={sectionName}
						isLoading={isLoading}
						defaultValues={defaultValues?.subscriptions}
						isNailSoftProduct={isNailSoftProduct}
						isCalendariseProduct={isCalendariseProduct}
						showMarketingServices={showMarketingServices}
						productType={productType?.name}
					/>
				</>
			)}

			{isBackEndPlatformOmaha || isBackEndPlatformNorth ? (
				<Transarmor sectionName={sectionName} isLoading={isLoading} />
			) : isBackEndPlatformTsys ? (
				<Tokenization sectionName={sectionName} isLoading={isLoading} />
			) : null}
		</div>
	);
};

export default PointOfSale;
